/**
 * Two Towns — Size chart
 * ---------------------------------------------------------------------------
 * <size-chart> custom element: a "Size chart" button that opens a native
 * <dialog> (centred modal on desktop, bottom sheet on mobile) with a cm / in
 * toggle. Measurements are stored in cm in the HTML (data-cm) and converted
 * to inches here, rounded to the nearest ½ in.
 *
 * Also renders inline (no popup) on the Size guide page; every chart on a
 * page switches together when the customer changes unit.
 *
 * The customer's unit choice is remembered across products and visits.
 * Safe to load more than once and safe across product section re-renders.
 */
(function () {
  'use strict';

  if (!window.customElements || customElements.get('size-chart')) return;

  var STORAGE_KEY = 'twotowns:size-unit';
  var CLOSE_DELAY_MS = 260;
  var reducedMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  function readUnit() {
    try { return localStorage.getItem(STORAGE_KEY); } catch (e) { return null; }
  }

  function saveUnit(unit) {
    try { localStorage.setItem(STORAGE_KEY, unit); } catch (e) { /* private mode */ }
  }

  function formatNumber(n) {
    var rounded = Math.round(n * 10) / 10;
    return String(rounded);
  }

  function cmToInches(cm) {
    return Math.round((cm / 2.54) * 2) / 2;
  }

  class SizeChart extends HTMLElement {
    connectedCallback() {
      if (this._ready) {
        // Moved in the DOM: listeners on this element are still attached.
        document.addEventListener('size-chart:unit', this._onUnitChange);
        return;
      }
      this._ready = true;

      // cm / in buttons (works in the popup and on the size guide page).
      this.addEventListener('click', function (event) {
        var unitButton = event.target.closest('[data-sc-unit]');
        if (unitButton && this.contains(unitButton)) {
          this.setUnit(unitButton.getAttribute('data-sc-unit'), true);
        }
      }.bind(this));

      // Keep every chart on the page on the same unit.
      this._onUnitChange = function (event) {
        if (event.detail && event.detail.source !== this) this.setUnit(event.detail.unit, false);
      }.bind(this);
      document.addEventListener('size-chart:unit', this._onUnitChange);

      this.setUnit(readUnit() || this.getAttribute('data-default-unit') || 'cm', false);

      // Popup behaviour (product pages only).
      this.dialog = this.querySelector('[data-sc-dialog]');
      this.trigger = this.querySelector('[data-sc-open]');
      if (!this.dialog || !this.trigger || typeof this.dialog.showModal !== 'function') return;

      this._closing = false;
      this.trigger.addEventListener('click', this.open.bind(this));

      this.dialog.addEventListener('click', function (event) {
        // A click on the dialog element itself is a click on the backdrop.
        if (event.target === this.dialog || event.target.closest('[data-sc-close]')) this.close();
      }.bind(this));

      // Esc: animate out instead of disappearing instantly.
      this.dialog.addEventListener('cancel', function (event) {
        event.preventDefault();
        this.close();
      }.bind(this));

      this.dialog.addEventListener('close', function () {
        document.documentElement.classList.remove('sc-scroll-lock');
        this.dialog.classList.remove('is-open');
        this._closing = false;
      }.bind(this));
    }

    disconnectedCallback() {
      if (this._onUnitChange) document.removeEventListener('size-chart:unit', this._onUnitChange);
    }

    open() {
      if (this.dialog.open) return;
      // Pick up a unit chosen on another product since this page loaded.
      var saved = readUnit();
      if (saved) this.setUnit(saved, false);

      document.documentElement.classList.add('sc-scroll-lock');
      this.dialog.showModal();

      var body = this.dialog.querySelector('.sc-dialog__body');
      if (body) body.scrollTop = 0;

      var dialog = this.dialog;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { dialog.classList.add('is-open'); });
      });
    }

    close() {
      if (!this.dialog || !this.dialog.open || this._closing) return;
      this._closing = true;
      this.dialog.classList.remove('is-open');

      var dialog = this.dialog;
      var finish = function () { if (dialog.open) dialog.close(); };

      if (reducedMotion && reducedMotion.matches) finish();
      else setTimeout(finish, CLOSE_DELAY_MS);
    }

    setUnit(unit, persist) {
      if (unit !== 'cm' && unit !== 'in') unit = 'cm';
      this.setAttribute('data-unit', unit);

      this.querySelectorAll('[data-cm]').forEach(function (cell) {
        var cm = parseFloat(cell.getAttribute('data-cm'));
        if (isNaN(cm)) return;
        cell.textContent = unit === 'cm' ? formatNumber(cm) : formatNumber(cmToInches(cm));
      });

      this.querySelectorAll('[data-sc-unit]').forEach(function (button) {
        button.setAttribute('aria-pressed', String(button.getAttribute('data-sc-unit') === unit));
      });

      this.querySelectorAll('[data-unit-only]').forEach(function (el) {
        el.hidden = el.getAttribute('data-unit-only') !== unit;
      });

      if (persist) {
        saveUnit(unit);
        document.dispatchEvent(new CustomEvent('size-chart:unit', { detail: { unit: unit, source: this } }));
      }
    }
  }

  customElements.define('size-chart', SizeChart);
})();
